import { useMemo, useState } from 'react'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { CalendarIcon } from 'lucide-react'

import { type DateRange, PRESET_DAYS, presetRange, toIso } from './presets'

import { AUDIT_LOG_MAX_WINDOW_DAYS } from '@/api/auditLogs'
import { Button } from '@/components/ui/button'
import { CalendarField } from '@/components/ui/calendar-field'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const formatDayLabel = (date: Date) => format(date, 'MMM d, yyyy')
const labelFor = (iso: string) => formatDayLabel(parseISO(iso))

interface DateRangePickerProps {
  value: DateRange
  onChange: (next: DateRange) => void
  today: Date
  disabled?: boolean
}

const DateRangePicker = ({ value, onChange, today, disabled = false }: DateRangePickerProps) => {
  const [open, setOpen] = useState(false)

  const activePresetDays = useMemo(() => {
    return (
      PRESET_DAYS.find((d) => {
        const range = presetRange(d, today)
        return value.start === range.start && value.end === range.end
      }) ?? null
    )
  }, [value, today])

  const triggerLabel = activePresetDays
    ? `Last ${activePresetDays} days`
    : `${labelFor(value.start)} – ${labelFor(value.end)}`

  const selectPreset = (days: number) => {
    onChange(presetRange(days, today))
    setOpen(false)
  }

  const startDate = parseISO(value.start)
  const endDate = parseISO(value.end)

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="w-[280px] justify-start font-normal">
          <CalendarIcon className="size-4 opacity-70" />
          <span className="flex-1 truncate text-left">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="flex flex-col gap-0.5 border-r p-2">
            {PRESET_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => selectPreset(days)}
                className={cn(
                  'hover:bg-accent rounded-md px-3 py-1.5 text-left text-sm whitespace-nowrap',
                  activePresetDays === days && 'bg-muted font-semibold'
                )}
              >
                Last {days} days
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-3 p-4">
            <CalendarField
              label="Start date"
              selected={startDate}
              formatLabel={formatDayLabel}
              onSelect={(day) => onChange({ start: toIso(day), end: value.end })}
              isDisabled={(day) =>
                differenceInCalendarDays(day, today) > 0 ||
                differenceInCalendarDays(day, endDate) > 0 ||
                differenceInCalendarDays(endDate, day) >= AUDIT_LOG_MAX_WINDOW_DAYS
              }
            />
            <CalendarField
              label="End date"
              selected={endDate}
              formatLabel={formatDayLabel}
              onSelect={(day) => onChange({ start: value.start, end: toIso(day) })}
              isDisabled={(day) =>
                differenceInCalendarDays(day, today) > 0 ||
                differenceInCalendarDays(day, startDate) < 0 ||
                differenceInCalendarDays(day, startDate) >= AUDIT_LOG_MAX_WINDOW_DAYS
              }
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangePicker
